/**
 * Live filtering and sorting.
 *
 * The server always renders a complete, working GET form: every filter URL is
 * real, shareable and crawlable, and submitting the form navigates to it. This
 * script intercepts that navigation and swaps the answer in instead — and only
 * once it has confirmed it can, at which point it hides the rail's Apply button
 * and reveals the mobile sheet. Any failure falls back to the navigation the
 * form was always going to do, so the customer is never stranded mid-filter.
 *
 * Two elements:
 *   <facet-filters>  the whole filter region; never itself replaced, so its
 *                    delegated listeners survive every swap
 *   <price-range>    the dual-handle slider, which drives the number inputs
 *                    rather than replacing them
 */
(function () {
  'use strict';

  if (!window.Theme) return;

  /** Regions replaced on every update. Order matters only for readability. */
  const SWAP_TARGETS = [
    '[data-facets-title-count]',
    '[data-facets-toolbar]',
    '[data-facets-chips]',
    '[data-facets-rail]',
    '[data-facets-sheet]',
    '[data-facets-results]',
  ];

  class FacetFilters extends window.Theme.ThemeElement {
    init() {
      this.results = this.querySelector('[data-facets-results]');
      if (!this.results || !this.querySelector('[data-facet-form]')) return;

      // Everything below this line is the enhancement claiming responsibility.
      this.setAttribute('data-js-ready', '');
      this.retireSubmits();

      this.queue = window.Theme.debounce(() => this.applyFromForm(), 300);

      this.on(this, 'change', (event) => {
        if (!event.target.closest('[data-facet-form]')) return;
        if (!event.target.name) return;
        this.activeForm = event.target.closest('[data-facet-form]');
        this.queue();
      });

      this.on(this, 'submit', (event) => {
        const form = event.target.closest('[data-facet-form]');
        if (!form) return;
        event.preventDefault();
        this.activeForm = form;
        this.apply(this.urlFor(form));
        this.closeSheet();
      });

      // Chips and "Clear all" are plain links to a filter URL. Same swap.
      this.on(this, 'click', (event) => {
        const link = event.target.closest('[data-facets-chips] a[href], [data-facet-clear]');
        if (!link) return;
        event.preventDefault();
        this.apply(link.href);
      });

      // Back and forward must undo a filter, not leave the page behind.
      this.on(window, 'popstate', () => this.apply(window.location.href, { push: false }));
    }

    /**
     * The rail's Apply becomes redundant once changes apply themselves. The
     * sheet keeps its button — the server labels it "Show N items" — because a
     * bottom sheet needs a way out that confirms what is waiting behind it.
     */
    retireSubmits() {
      this.querySelectorAll('[data-facet-submit]').forEach((button) => {
        button.hidden = !button.closest('[data-facets-sheet]');
      });
    }

    closeSheet() {
      const sheet = document.getElementById('FacetSheet');
      if (sheet && typeof sheet.close === 'function') sheet.close();
    }

    /** Build the URL this form would have navigated to. */
    urlFor(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      const url = new URL(window.location.pathname, window.location.origin);
      url.search = params.toString();
      return url.toString();
    }

    applyFromForm() {
      const form = this.activeForm || this.querySelector('[data-facet-form]');
      if (form) this.apply(this.urlFor(form));
    }

    /**
     * Which disclosure groups are open, keyed per copy of the form. The server
     * opens a group only when it has an active value, so without this a group
     * the customer expanded to browse would snap shut under them on every
     * change.
     */
    disclosureState() {
      const state = {};
      this.querySelectorAll('details[data-facet-key]').forEach((details) => {
        state[this.disclosureKey(details)] = details.open;
      });
      return state;
    }

    disclosureKey(details) {
      const scope = details.closest('[data-facets-sheet]') ? 'sheet' : 'rail';
      return scope + ':' + details.dataset.facetKey;
    }

    restoreDisclosures(state) {
      this.querySelectorAll('details[data-facet-key]').forEach((details) => {
        const key = this.disclosureKey(details);
        if (key in state) details.open = state[key];
      });
    }

    async apply(url, options) {
      const push = !options || options.push !== false;

      if (this.request) this.request.abort();
      this.request = new AbortController();

      const disclosures = this.disclosureState();
      const focusedId = document.activeElement ? document.activeElement.id : null;
      this.setBusy(true);

      try {
        const doc = await window.Theme.fetchDocument(url, this.request.signal);
        SWAP_TARGETS.forEach((selector) => window.Theme.swap(doc, selector));

        this.restoreDisclosures(disclosures);
        this.retireSubmits();

        if (push) window.history.pushState({ facets: true }, '', url);

        // The swap destroyed the node that had focus; put it back on its
        // replacement so keyboard users keep their place in the list.
        if (focusedId) {
          const restored = document.getElementById(focusedId);
          if (restored) restored.focus({ preventScroll: true });
        }

        const count = document.querySelector('[data-facets-title-count]');
        if (count) window.Theme.announce(count.textContent.trim());
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Fall back to the navigation the form was always going to do.
        window.location.href = url;
      } finally {
        this.request = null;
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      if (busy) {
        this.results.setAttribute('aria-busy', 'true');
      } else {
        this.results.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Two <input type="range"> overlaid on one rail, driving the two number
   * inputs that actually carry the filter's parameter names. Shopify gives a
   * min and a max parameter and no slider, so this is the whole of it.
   *
   * A value at either extreme is written back as an empty string: "from 0 to
   * the maximum" is not a filter, and submitting it as one would put a
   * meaningless chip on the page.
   */
  class PriceRange extends window.Theme.ThemeElement {
    init() {
      this.minThumb = this.querySelector('[data-range-thumb="min"]');
      this.maxThumb = this.querySelector('[data-range-thumb="max"]');
      this.fill = this.querySelector('[data-range-fill]');

      const body = this.closest('.facet__body');
      this.minNumber = body && body.querySelector('[data-range-number="min"]');
      this.maxNumber = body && body.querySelector('[data-range-number="max"]');

      this.ceiling = Number(this.dataset.rangeCeiling);
      if (!this.minThumb || !this.maxThumb || !this.minNumber || !this.maxNumber) return;
      if (!(this.ceiling > 0)) return;

      this.formatter = this.buildFormatter();

      this.on(this.minThumb, 'input', () => this.fromThumbs('min'));
      this.on(this.maxThumb, 'input', () => this.fromThumbs('max'));
      this.on(this.minThumb, 'change', () => this.commit());
      this.on(this.maxThumb, 'change', () => this.commit());
      this.on(this.minNumber, 'input', () => this.fromNumbers());
      this.on(this.maxNumber, 'input', () => this.fromNumbers());

      this.fromNumbers();
      this.hidden = false;
    }

    /**
     * Only ever used for `aria-valuetext`. Every price a customer can see is
     * still rendered by Liquid's `money` filter — there is simply no Liquid at
     * the moment a handle moves, and a slider with no announced value is
     * unusable with a screen reader.
     */
    buildFormatter() {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: this.dataset.currency,
          maximumFractionDigits: 0,
        });
      } catch (error) {
        return null;
      }
    }

    label(value) {
      return this.formatter ? this.formatter.format(value) : String(value);
    }

    fromThumbs(moved) {
      let min = Number(this.minThumb.value);
      let max = Number(this.maxThumb.value);

      // Clamp the handle that moved, never drag the other one along with it.
      if (moved === 'min' && min > max) {
        min = max;
        this.minThumb.value = String(min);
      }
      if (moved === 'max' && max < min) {
        max = min;
        this.maxThumb.value = String(max);
      }

      this.minNumber.value = min > 0 ? String(min) : '';
      this.maxNumber.value = max < this.ceiling ? String(max) : '';
      this.paint(min, max);
    }

    fromNumbers() {
      const min = this.clamp(this.minNumber.value, 0);
      const max = this.clamp(this.maxNumber.value, this.ceiling);
      this.minThumb.value = String(Math.min(min, max));
      this.maxThumb.value = String(Math.max(min, max));
      this.paint(Number(this.minThumb.value), Number(this.maxThumb.value));
    }

    clamp(raw, fallback) {
      const value = parseFloat(raw);
      if (isNaN(value)) return fallback;
      return Math.min(Math.max(Math.round(value), 0), this.ceiling);
    }

    paint(min, max) {
      if (this.fill) {
        this.fill.style.setProperty('--range-start', (min / this.ceiling) * 100 + '%');
        this.fill.style.setProperty('--range-end', 100 - (max / this.ceiling) * 100 + '%');
      }
      this.minThumb.setAttribute('aria-valuetext', this.label(min));
      this.maxThumb.setAttribute('aria-valuetext', this.label(max));

      // When both handles sit on the same spot the later one in the DOM wins
      // every pointer event. Lift the minimum out from under it near the top.
      this.minThumb.style.zIndex = min > this.ceiling / 2 ? '2' : '';
    }

    /** Hand off to <facet-filters>, which owns every request. */
    commit() {
      this.minNumber.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
  if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);
})();
